<template>
  <div>
    <!-- Information -->
    <div>
      <h6 class="ts-uppercase ts-bg-gray-200 ts-p-2">
        {{ $t('wholesale.information') }}
      </h6>
      <div class="ts-p-3 ts-rounded">
        <div class="ts-grid ts-grid-cols-1 xl:ts-grid-cols-3 ts-gap-3">
          <div class="ts-flex ts-space-x-3">
            <p>{{ $t('wholesale.date') }}:</p>
            <p>
              <strong>{{ record.stock_out_date }}</strong>
            </p>
          </div>
          <div class="ts-flex ts-space-x-3">
            <p>{{ $t('wholesale.invoiceNumber') }}:</p>
            <p>
              <strong>{{ record.invoice_no }}</strong>
            </p>
          </div>
          <div class="ts-flex ts-space-x-3">
            <p>{{ $t('wholesale.reference') }}:</p>
            <p>
              <strong>{{
                record.reference_no ? record.reference_no : 'N/A'
              }}</strong>
            </p>
          </div>
          <div class="ts-flex ts-space-x-3">
            <p>{{ $t('wholesale.creditDay') }}:</p>
            <p>
              <strong>{{ record.credit_day ? record.credit_day : '0' }}</strong>
            </p>
          </div>
          <div class="ts-flex ts-space-x-3">
            <p>{{ $t('wholesale.seller') }}:</p>
            <p>
              <strong>{{
                record.sale ? record.sale.employee_name_en : 'N/A'
              }}</strong>
            </p>
          </div>
          <div class="ts-flex ts-space-x-3">
            <p>{{ $t('wholesale.terms') }} :</p>
            <p>
              <strong>{{
                record.sale_contract ? record.sale_contract.project_name : 'N/A'
              }}</strong>
            </p>
          </div>
        </div>
      </div>
    </div>
    <!-- Bill To -->
    <div class="ts-mb-4">
      <h6 class="ts-uppercase ts-bg-gray-200 ts-p-2">
        {{ $t('wholesale.billTo') }}
      </h6>
      <div class="ts-p-3 ts-rounded">
        <div class="ts-grid ts-grid-cols-1 xl:ts-grid-cols-3 ts-gap-3">
          <div class="ts-flex ts-space-x-3">
            <strong>
              {{ record.customer ? record.customer.customer_name : '' }}
            </strong>
            <strong>
              {{ record.customer ? record.customer.address : '' }}
            </strong>
            <strong>
              {{ record.customer ? record.customer.contact_number : '' }}
            </strong>
            <strong>
              {{ record.customer ? record.customer.contact_email : '' }}
            </strong>
          </div>
        </div>
      </div>
    </div>
    <!-- Item -->
    <div>
      <h6 class="ts-uppercase ts-bg-gray-200 ts-p-2">
        {{ $t('wholesale.item') }}
      </h6>
      <div class="ts-rounded">
        <div class="ts-overflow-y-scroll">
          <table class="ts-w-full">
            <thead>
              <tr class="ts-bg-gray-200 ts-h-10 ts-sticky ts-top-0">
                <th style="width: 2%" class="ts-pl-2 ts-whitespace-no-wrap">
                  {{ $t('wholesale.no') }}
                </th>
                <th style="width: 14%" class="ts-pl-4 ts-whitespace-no-wrap">
                  {{ $t('wholesale.barcode') }}
                </th>
                <th style="width: 25%" class="ts-whitespace-no-wrap">
                  {{ $t('wholesale.description') }}
                </th>
                <th
                  style="width: 4%"
                  class="ts-whitespace-no-wrap ts-text-center"
                >
                  {{ $t('wholesale.qty') }}
                </th>
                <th
                  style="width: 10%"
                  class="ts-whitespace-no-wrap ts-text-center"
                >
                  {{ $t('wholesale.uom') }}
                </th>
                <th style="width: 10%" class="ts-whitespace-no-wrap">
                  {{ $t('wholesale.unitPriceTHB') }}
                </th>
                <th style="width: 10%" class="ts-whitespace-no-wrap">
                  {{ $t('wholesale.unitPrice') }}
                </th>
                <th
                  style="width: 4%"
                  class="ts-whitespace-no-wrap ts-text-center"
                >
                  {{ $t('wholesale.discount') }}
                </th>
                <th
                  style="width: 14%"
                  class="ts-whitespace-no-wrap ts-text-center"
                >
                  {{ $t('wholesale.amountTHB') }}
                </th>
                <th
                  style="width: 14%"
                  class="ts-whitespace-no-wrap ts-text-center"
                >
                  {{ $t('wholesale.amount') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="(item, index) in record.details">
                <tr
                  :key="index"
                  class="ts-cursor-pointer hover:ts-bg-gray-300 ts-w-full ts-h-10"
                >
                  <td class="ts-text-center">
                    <b>{{ index + 1 }}</b>
                  </td>
                  <td class="ts-whitespace-no-wrap ts-pl-4">
                    <b>{{ item.barcode }}</b>
                  </td>
                  <td class="ts-whitespace-no-wrap">
                    <b>{{ item.product_name_en }}</b>
                  </td>
                  <td class="ts-text-center">
                    <b>{{ item.quantity }}</b>
                  </td>
                  <td class="ts-text-center">
                    <b>{{ item.uom_name_en }}</b>
                  </td>
                  <td>
                    <div
                      class="ts-flex ts-justify-between ts-white-space-nowrap"
                    >
                      <b class="ts-pl-2">฿</b>
                      <b class="ts-pr-8">
                        {{
                          format_number(item.unit_price / record.fx_rate_thb)
                        }}
                      </b>
                    </div>
                  </td>
                  <td>
                    <div
                      class="ts-flex ts-justify-between ts-white-space-nowrap"
                    >
                      <b>$</b>
                      <b class="ts-pr-16">{{
                        format_number(item.unit_price)
                      }}</b>
                    </div>
                  </td>
                  <td>
                    <div
                      class="ts-flex ts-justify-between ts-white-space-nowrap"
                    >
                      <b>{{ item.discount_type }}</b>
                      <b>{{ item.discount_amount }}</b>
                    </div>
                  </td>
                  <td class="ts-text-right ts-pr-12">
                    <div
                      class="ts-flex ts-justify-between ts-white-space-nowrap"
                    >
                      <b class="ts-pl-12">฿</b>
                      <b>
                        {{
                          format_number(item.grand_total)
                        }}
                      </b>
                    </div>
                  </td>
                  <td>
                    <div
                      class="ts-flex ts-justify-between ts-white-space-nowrap"
                    >
                      <b class="ts-pl-2">$</b>
                      <b class="ts-pr-2">{{
                        format_number(item.grand_total * record.fx_rate_thb)
                      }}</b>
                    </div>
                  </td>
                </tr>
                <tr>
                  <td></td>
                  <td colspan="9" v-for="(subData, index) in item.serial_list">
                    <b> {{ item.serial_list == 0 ? '' : subData.label }} </b>
                  </td>
                </tr>
              </template>
              <tr>
                <td colspan="6"></td>
                <td colspan="2" class="ts-bg-gray-200 ts-pl-4 ts-pt-2">
                  <b>SUBTOTAL</b>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-12">฿</b>
                    <b class="ts-pr-12">{{
                      format_number(record.sub_total)
                    }}</b>
                  </div>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-2">$</b>
                    <b class="ts-pr-2">{{ format_number(record.sub_total * record.fx_rate_thb) }}</b>
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="6"></td>
                <td colspan="2" class="ts-bg-gray-200 ts-pl-4"><b>VAT</b></td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-12">฿</b>
                    <b class="ts-pr-12">{{
                      format_number(record.vat_amount)
                    }}</b>
                  </div>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-2">$</b>
                    <b class="ts-pr-2">{{
                      format_number(record.vat_amount * record.fx_rate_thb)
                    }}</b>
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="6"></td>
                <td colspan="2" class="ts-bg-gray-200 ts-pl-4"><b>TOTAL</b></td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-12">฿</b>
                    <b class="ts-pr-12">{{
                      format_number(record.grand_total)
                    }}</b>
                  </div>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-2">$</b>
                    <b class="ts-pr-2">{{
                      format_number(record.grand_total * record.fx_rate_thb)
                    }}</b>
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="6"></td>
                <td colspan="2" class="ts-bg-gray-200 ts-pl-4">
                  <b>PAID AMOUNT</b>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-12">฿</b>
                    <b class="ts-pr-12">{{
                      format_number(record.amount_paid)
                    }}</b>
                  </div>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-2">$</b>
                    <b class="ts-pr-2">{{
                      format_number(record.amount_paid * record.fx_rate_thb)
                    }}</b>
                  </div>
                </td>
              </tr>
              <tr>
                <td colspan="6"></td>
                <td colspan="2" class="ts-bg-gray-200 ts-pl-4 ts-pb-2">
                  <b>DUE AMOUNT</b>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-12">฿</b>
                    <b class="ts-pr-12">{{
                      format_number(record.amount_due)
                    }}</b>
                  </div>
                </td>
                <td class="ts-bg-gray-200 ts-text-right">
                  <div class="ts-flex ts-justify-between ts-white-space-nowrap">
                    <b class="ts-pl-2">$</b>
                    <b class="ts-pr-2">{{
                      format_number(record.amount_due * record.fx_rate_thb)
                    }}</b>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ViewWholeSale',
  props: ['record'],
  methods: {
    format_number(value) {
      return parseFloat(value).toFixed(2)
    }
  }
}
</script>
